import { useEffect, useRef, useState } from "react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Loader2, Trash2 } from "lucide-react";

export function PhotoGallery({ bookingId, photos, onDelete }) {
  if (!photos?.length) return <p className="text-sm text-muted-foreground">No completion photos yet.</p>;
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="photo-gallery">
      {photos.map((p) => (
        <div key={p.photo_id} className="relative group">
          <img src={`${API_BASE}/bookings/${bookingId}/photos/${p.photo_id}`} alt="Completion photo"
            className="aspect-square w-full object-cover rounded-xl border border-border" data-testid={`photo-${p.photo_id}`} />
          {onDelete && (
            <button onClick={() => onDelete(p.photo_id)} className="absolute top-1 right-1 p-1.5 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity" data-testid={`photo-delete-${p.photo_id}`} aria-label="Delete photo">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

export default function CompletionPhotos({ bookingId, canUpload }) {
  const [photos, setPhotos] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/bookings/${bookingId}/photos`);
      setPhotos(data.photos);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bookingId]);

  const upload = async (files) => {
    for (let i = 0; i < files.length; i++) {
      const fd = new FormData();
      fd.append("file", files[i]);
      try {
        await api.post(`/bookings/${bookingId}/photos`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => setProgress({
            index: i + 1, total: files.length,
            pct: e.total ? Math.round((e.loaded / e.total) * 100) : 0,
          }),
        });
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    }
    setProgress(null);
    if (fileRef.current) fileRef.current.value = "";
    toast.success("Photos uploaded");
    load();
  };

  const remove = async (photoId) => {
    try { await api.delete(`/bookings/${bookingId}/photos/${photoId}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading) return <Loader2 className="h-4 w-4 animate-spin text-accent" />;

  return (
    <div className="space-y-3" data-testid="completion-photos">
      <PhotoGallery bookingId={bookingId} photos={photos} onDelete={canUpload ? remove : null} />
      {canUpload && (
        <>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
            onChange={(e) => e.target.files?.length && upload(Array.from(e.target.files))} data-testid="photo-input" />
          <button onClick={() => fileRef.current?.click()} disabled={!!progress}
            className="w-full sm:w-auto px-5 py-2.5 rounded-full border border-border font-medium text-sm hover:bg-secondary transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="photo-upload-btn">
            {progress ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
            {progress ? `Uploading ${progress.index}/${progress.total} — ${progress.pct}%` : "Add completion photos"}
          </button>
          {progress && (
            <div className="h-2 rounded-full bg-secondary overflow-hidden" data-testid="photo-progress">
              <div className="h-full bg-accent transition-all" style={{ width: `${progress.pct}%` }} />
            </div>
          )}
          <p className="text-xs text-muted-foreground">JPG, PNG, WEBP or HEIC · up to 10MB each</p>
        </>
      )}
    </div>
  );
}
